import * as React from "react";
import * as s from "./Avatar.style.css";
import type { AvatarProps } from "./Avatar.types";

export type { AvatarProps } from "./Avatar.types";

// Color palette for avatar backgrounds
// These colors are chosen to be vibrant, distinct, and work well with white text
const AVATAR_COLORS = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#EF4444", // Red
  "#F59E0B", // Amber
  "#10B981", // Green
  "#06B6D4", // Cyan
  "#6366F1", // Indigo
  "#F97316", // Orange
  "#14B8A6", // Teal
  "#A855F7", // Violet
  "#E11D48", // Rose
] as const;

/**
 * Generate a consistent color for a given name
 * Uses a simple hash function to map names to colors
 */
function getColorForName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

/**
 * Get initials from first and last name
 */
function getInitials(firstName: string, lastName: string): string {
  const firstInitial = firstName?.charAt(0)?.toUpperCase() || "";
  const lastInitial = lastName?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
}

export const Avatar = ({
  email,
  firstName,
  lastName,
  image: imageProp,
  borderColor,
  size = 32,
}: AvatarProps) => {
  const image = typeof imageProp === "string" && imageProp.trim().length > 0 ? imageProp.trim() : undefined;
  const [imageLoadFailed, setImageLoadFailed] = React.useState(false);
  React.useEffect(() => {
    setImageLoadFailed(false);
  }, [image]);

  const initials = React.useMemo(
    () => getInitials(firstName, lastName),
    [firstName, lastName]
  );

  const showImage = image && !imageLoadFailed;
  const backgroundColor = React.useMemo(() => {
    if (showImage) return undefined;
    const fullName = `${firstName}${lastName}`;
    return getColorForName(fullName);
  }, [firstName, lastName, showImage]);

  // Calculate font size proportionally (12px for 32px avatar)
  const fontSize = React.useMemo(() => {
    return `${Math.round((size / 32) * 12)}px`;
  }, [size]);

  const containerStyle = React.useMemo(
    () =>
      ({
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor,
        borderColor: borderColor || "transparent",
      } as React.CSSProperties),
    [size, backgroundColor, borderColor]
  );

  const initialsStyle = React.useMemo(
    () =>
      ({
        fontSize,
      } as React.CSSProperties),
    [fontSize]
  );

  const displayName = `${firstName} ${lastName}`;
  const ariaLabel = `${displayName}${email ? ` (${email})` : ""}`;

  return (
    <div
      className={s.avatarContainer}
      style={containerStyle}
      role="img"
      aria-label={ariaLabel}
    >
      {showImage ? (
        <img
          src={image}
          alt=""
          className={s.avatarImage}
          referrerPolicy="no-referrer"
          onError={() => setImageLoadFailed(true)}
        />
      ) : (
        <div className={s.avatarInitials} style={initialsStyle} aria-hidden="true">
          {initials}
        </div>
      )}
    </div>
  );
};

